import { useCallback, useEffect, useState } from "react";
import { Checkbox } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner,
  faPlus,
  faRotateRight,
  faLock,
  faHashtag,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { authFetch } from "../utils/api";
import { guardWrite } from "../utils/permissions";
import SlackLogo from "./SlackLogo";
import SlackConnectModal from "./SlackConnectModal";
import "./SlackNotificationLinker.css";

/**
 * SlackNotificationLinker — Notification Center sub-component for Slack.
 *
 * Behaviour:
 *  * Lists every Slack workspace the user has connected via
 *    messaging_connections. For each workspace, lists postable channels
 *    (public + private-where-bot-is-a-member) returned by the backend's
 *    /slack_channels endpoint.
 *  * Each channel row is a checkbox. Selection is held by the parent
 *    (`selectedDestinations` is a flat list of "team_id:channel_id"
 *    strings); this component only reports toggles back via
 *    `onToggleDestination(destinationKey)`.
 *  * If the user has zero Slack workspaces, the panel shows a
 *    "Connect Slack" CTA that opens the standard SlackConnectModal
 *    (the same modal Connect Messaging Platforms uses, no duplicate UI).
 *  * If ≥1 workspace is connected, a small "Add another Slack workspace"
 *    button at the bottom opens the same modal.
 */
function SlackNotificationLinker({
  selectedDestinations,  // List<string>, each entry "team_id:channel_id"
  onToggleDestination,   // (destinationKey: string) => void
}) {
  const { t } = useTranslation();
  const [workspaces, setWorkspaces] = useState([]);
  // { [team_id]: [{ id, name, is_private, is_member }, ...] }
  const [channelsByWorkspace, setChannelsByWorkspace] = useState({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [connectModalOpen, setConnectModalOpen] = useState(false);
  const [error, setError] = useState(null);

  const fetchWorkspaces = useCallback(async () => {
    try {
      const userId = localStorage.getItem("user_id");
      if (!userId) return [];
      const res = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}messaging_connections?user_id=${userId}`
      );
      if (!res || !res.ok) return [];
      const all = await res.json();
      return (Array.isArray(all) ? all : []).filter((c) => c.platform === "slack");
    } catch (err) {
      console.error("Error fetching Slack workspaces:", err);
      return [];
    }
  }, []);

  const fetchChannels = useCallback(async (connectionId) => {
    try {
      const res = await authFetch(
        `${process.env.REACT_APP_API_BASE_URL}messaging_connections/${connectionId}/slack_channels`
      );
      if (!res || !res.ok) return null;
      return await res.json();
    } catch (err) {
      console.error("Error fetching Slack channels:", err);
      return null;
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const wsList = await fetchWorkspaces();
      setWorkspaces(wsList);

      // Parallel-fetch channels for every workspace. A slow or 4xx response
      // for one workspace doesn't block the others — its dropdown just stays
      // empty until the user clicks refresh.
      const results = await Promise.all(
        wsList.map(async (ws) => {
          const data = await fetchChannels(ws.id);
          return [ws.external_id, data?.channels || []];
        })
      );
      const next = {};
      for (const [teamId, channels] of results) {
        next[teamId] = channels;
      }
      setChannelsByWorkspace(next);
    } catch (err) {
      console.error("Slack workspaces/channels fetch failed:", err);
      setError(err.message || "Failed to load Slack workspaces");
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [fetchWorkspaces, fetchChannels]);

  useEffect(() => {
    refreshAll();
  }, [refreshAll]);

  const handleSlackConnected = () => {
    setConnectModalOpen(false);
    refreshAll();
  };

  const isSelected = (teamId, channelId) =>
    Array.isArray(selectedDestinations) &&
    selectedDestinations.includes(`${teamId}:${channelId}`);

  const handleToggle = (teamId, channelId) => {
    if (!guardWrite()) return;
    onToggleDestination(`${teamId}:${channelId}`);
  };

  return (
    <div
      className="notification-emails-section slack-notify-linker"
      style={{
        // Aubergine — matches the Slack accent used elsewhere in the
        // portal (Connect Messaging Platforms, the SlackConnectModal
        // header bar) so the visual identity is consistent.
        "--brand-color": "#611f69",
        "--brand-bg": "rgba(97, 31, 105, 0.10)",
      }}
    >
      <div className="notification-emails-section-heading">
        <span className="notification-emails-section-heading-icon">
          <SlackLogo size={18} />
        </span>
        <span className="slack-notify-linker-heading-label">
          {t("notify_section_slack_recipients") || "Slack channels"}
        </span>
        {workspaces.length > 0 && (
          <button
            type="button"
            className="slack-notify-refresh-btn"
            onClick={refreshAll}
            disabled={refreshing}
            title={t("refresh") || "Refresh"}
          >
            <FontAwesomeIcon icon={refreshing ? faSpinner : faRotateRight} spin={refreshing} />
          </button>
        )}
      </div>

      {loading ? (
        <div className="slack-notify-loading">
          <FontAwesomeIcon icon={faSpinner} spin />
        </div>
      ) : workspaces.length === 0 ? (
        <div className="slack-notify-empty">
          <p>
            {t("notify_slack_empty") ||
              "Connect your Slack workspace so notifications can post into one of your channels."}
          </p>
          <button
            type="button"
            className="slack-notify-connect-btn"
            onClick={() => {
              if (!guardWrite()) return;
              setConnectModalOpen(true);
            }}
          >
            <SlackLogo size={14} style={{ marginRight: 6 }} />
            {t("connect_slack") || "Connect Slack"}
          </button>
        </div>
      ) : (
        <>
          {workspaces.map((ws) => {
            const channels = channelsByWorkspace[ws.external_id] || [];
            // Public channels are always postable (we requested
            // chat:write.public). For private channels, the bot must be a
            // member — anything else would fail at chat.postMessage time.
            const visible = channels.filter((ch) => !ch.is_private || ch.is_member);
            return (
              <div key={ws.id} className="slack-notify-workspace">
                <div className="slack-notify-workspace-header">
                  <span className="slack-notify-workspace-name">
                    {ws.display_name || ws.external_id}
                  </span>
                </div>
                {visible.length === 0 ? (
                  <div className="slack-notify-no-channels">
                    {t("notify_slack_no_channels") ||
                      "No channels yet. Invite the bot to a channel first."}
                  </div>
                ) : (
                  <div className="slack-notify-channels">
                    {visible.map((ch) => (
                      <label key={ch.id} className="slack-notify-channel-row">
                        <Checkbox
                          checked={isSelected(ws.external_id, ch.id)}
                          onChange={() => handleToggle(ws.external_id, ch.id)}
                          size="small"
                          sx={{
                            p: 0.5,
                            color: "#c4c4c4",
                            "&.Mui-checked": { color: "#5616ea" },
                          }}
                        />
                        <FontAwesomeIcon
                          icon={ch.is_private ? faLock : faHashtag}
                          className="slack-notify-channel-icon"
                        />
                        <span className="slack-notify-channel-name">{ch.name}</span>
                        {ch.is_private && (
                          <span className="slack-notify-channel-badge">
                            {t("private") || "Private"}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          <button
            type="button"
            className="slack-notify-add-workspace-btn"
            onClick={() => {
              if (!guardWrite()) return;
              setConnectModalOpen(true);
            }}
          >
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: 6 }} />
            {t("notify_slack_add_workspace") || "Add another Slack workspace"}
          </button>
        </>
      )}

      {error && (
        <div className="slack-notify-error">
          {error}
        </div>
      )}

      {workspaces.length > 0 && (
        <div className="slack-notify-hint">
          <FontAwesomeIcon icon={faCircleInfo} className="slack-notify-hint-icon" />
          <span>
            {t("notify_slack_invite_hint") ||
              "Public channels work automatically. For a private channel, invite the bot via /invite @YourBotName in that channel, then click refresh."}
          </span>
        </div>
      )}

      <SlackConnectModal
        open={connectModalOpen}
        onClose={() => setConnectModalOpen(false)}
        onConnected={handleSlackConnected}
      />
    </div>
  );
}

export default SlackNotificationLinker;
